import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { captureError } from "@/lib/sentry";
import { BellIcon, XIcon } from "@phosphor-icons/react";

export default function AlertsBell() {
  const [data, setData] = useState({ alerts: [], unread: 0 });
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/alerts");
      setData(r.data);
    } catch (err) {
      if (err?.response?.status !== 401) captureError("alerts load failed:", err);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  const markRead = async () => {
    try {
      await api.post("/alerts/read", {});
      setData((d) => ({ alerts: d.alerts.map((a) => ({ ...a, read: true })), unread: 0 }));
    } catch (err) {
      captureError("alerts read failed:", err);
    }
  };

  return (
    <div className="relative">
      <button
        onClick={() => { setOpen((o) => !o); if (!open && data.unread) markRead(); }}
        data-testid="alerts-bell"
        className="relative text-white/60 hover:text-white p-1.5"
      >
        <BellIcon weight={data.unread ? "fill" : "regular"} size={20} className={data.unread ? "text-[#FDB927]" : ""} />
        {data.unread > 0 && (
          <span data-testid="alerts-unread-badge" className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-[#FF453A] text-white text-[9px] font-black flex items-center justify-center">
            {data.unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-[88vw] max-w-[360px] rounded-lg bg-[#0d0d0d] border border-white/15 shadow-2xl z-[60] overflow-hidden" data-testid="alerts-panel">
          <div className="px-4 py-3 flex items-center justify-between border-b border-white/10 bg-[#121212]">
            <p className="font-display font-bold text-sm uppercase tracking-tight">Storyline Alerts</p>
            <button onClick={() => setOpen(false)} className="text-white/40 hover:text-white" data-testid="alerts-close"><XIcon size={16} /></button>
          </div>
          <div className="max-h-[60vh] overflow-y-auto divide-y divide-white/5">
            {data.alerts.length === 0 && <p className="px-4 py-6 text-center text-white/40 text-xs" data-testid="alerts-empty">No alerts right now.</p>}
            {data.alerts.map((a) => (
              <Link
                key={a.id}
                to={`/watch/${a.game_id}`}
                onClick={() => setOpen(false)}
                data-testid={`alert-${a.id}`}
                className="block px-4 py-3 hover:bg-white/[0.03] transition-colors"
              >
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-[9px] font-black uppercase tracking-[0.15em] px-1.5 py-0.5 rounded"
                    style={{ backgroundColor: `${a.accent}1f`, color: a.accent, border: `1px solid ${a.accent}55` }}>{a.title}</span>
                  <span className="text-[9px] font-bold uppercase tracking-wider text-white/30">{a.league}</span>
                </div>
                <p className="text-xs text-white/75 leading-snug">{a.body}</p>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
